"use client"

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Search as SearchIcon } from 'lucide-react'
import React from 'react'

export type NewsCategory = {
  slug: string
  title: string
}

export type NewsHeroProps = {
  categories: NewsCategory[]
  selectedCategory?: string | null
  activeCategory?: string | null
  searchTerm: string
  onSearchTermChange: (value: string) => void
  onSelectCategory: (slug: string | null) => void
}

export const NewsHero: React.FC<NewsHeroProps> = ({
  categories,
  selectedCategory,
  activeCategory,
  searchTerm,
  onSearchTermChange,
  onSelectCategory,
}) => {
  const resolvedCategory = selectedCategory ?? activeCategory ?? null

  return (
    <section className="bg-minigreen-7 dark:bg-minigreen-bg-dark text-mini-dark-primary">
      <div className="container py-10 min-h-[300px] flex items-end">
        <div className="max-w-xl">
          <h1 className="text-mini-dark-primary text-[3.5rem] mb-6">News</h1>
          <p className="mt-4 text-lg text-mini-dark-secondary">
            See what’s growing at MiniNature Reserve with our latest conservation efforts, community stories, and
            native plant insights.
          </p>
        </div>
      </div>
      <div className="border-t border-minigreen-6/40 dark:border-minigreen-bg-dark/40 bg-minigreen-bg-light dark:bg-mini-black">
        <div className="container flex flex-col gap-6 py-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-xl">
            <Label htmlFor="news-search" className="sr-only">
              Search news articles
            </Label>
            <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-minigreen-6" />
            <Input
              id="news-search"
              name="q"
              placeholder="Search for an article..."
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
              className="h-12 rounded-full border-minigreen-4 dark:border-minigreen-5 bg-mini-white dark:bg-mini-black pl-10 pr-4 text-base text-mini-primary dark:text-mini-dark-primary"
              type="search"
            />
          </div>

          <nav className="flex flex-wrap items-center gap-3">
            <CategoryPill isActive={!resolvedCategory} onClick={() => onSelectCategory(null)}>
              All Posts
            </CategoryPill>
            {categories.map(({ slug, title }) => (
              <CategoryPill
                key={slug}
                isActive={resolvedCategory === slug}
                onClick={() => onSelectCategory(slug)}
              >
                {title}
              </CategoryPill>
            ))}
          </nav>
        </div>
      </div>
    </section>
  )
}

type CategoryPillProps = {
  children: React.ReactNode
  isActive?: boolean
  onClick: () => void
}

const CategoryPill: React.FC<CategoryPillProps> = ({ children, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-minigreen-6 ${
        isActive
          ? 'border-minigreen-6 bg-minigreen-6 text-mini-dark-primary shadow-sm'
          : 'border-minigreen-4 bg-white/60 text-mini-primary hover:bg-white'
      }`}
    >
      {children}
    </button>
  )
}
